// PWA Utility for iOS and Android installation

/**
 * Detect if the app is running as a PWA
 */
export const isPWA = () => {
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone === true;
};

/**
 * Detect iOS device
 */
export const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

/**
 * Detect if iOS Safari (the only browser that supports PWA on iOS)
 */
export const isIOSSafari = () => {
    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua);
    const webkit = /WebKit/.test(ua);
    const notChrome = !/CriOS|FxiOS|EdgiOS/.test(ua); // Not Chrome, Firefox, or Edge on iOS
    return iOS && webkit && notChrome;
};

/**
 * Detect Android device
 */
export const isAndroid = () => {
    return /Android/.test(navigator.userAgent);
};

/**
 * Check if PWA can be installed (beforeinstallprompt event support)
 */
export const canInstallPWA = () => {
    return 'BeforeInstallPromptEvent' in window;
};

/**
 * Check iOS version
 */
export const getIOSVersion = () => {
    const match = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
    if (match) {
        return {
            major: parseInt(match[1], 10),
            minor: parseInt(match[2], 10),
            patch: parseInt(match[3] || 0, 10)
        };
    }
    return null;
};

/**
 * Check if iOS version supports Web Push (iOS 16.4+)
 */
export const iosSupportsWebPush = () => {
    const version = getIOSVersion();
    if (!version) return false;

    // iOS 16.4 or higher supports Web Push
    if (version.major > 16) return true;
    if (version.major === 16 && version.minor >= 4) return true;

    return false;
};

/**
 * Check if notifications are supported
 */
export const notificationsSupported = () => {
    return 'Notification' in window && 'serviceWorker' in navigator;
};

/**
 * Get notification permission status
 */
export const getNotificationPermission = () => {
    if (!notificationsSupported()) return 'unsupported';
    return Notification.permission;
};

/**
 * Show iOS installation instructions
 */
export const showIOSInstallInstructions = () => {
    return {
        title: 'Install App on iOS',
        steps: [
            'Tap the Share button (square with arrow) at the bottom of Safari',
            'Scroll down and tap "Add to Home Screen"',
            'Tap "Add" to install the app',
            'Open the app from your home screen to receive notifications'
        ]
    };
};

/**
 * Get device info for debugging
 */
export const getDeviceInfo = () => {
    return {
        isPWA: isPWA(),
        isIOS: isIOS(),
        isIOSSafari: isIOSSafari(),
        isAndroid: isAndroid(),
        iosVersion: getIOSVersion(),
        iosSupportsWebPush: iosSupportsWebPush(),
        notificationsSupported: notificationsSupported(),
        notificationPermission: getNotificationPermission(),
        userAgent: navigator.userAgent,
        displayMode: window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser'
    };
};

/**
 * PWA Install Prompt Handler
 */
let deferredPrompt = null;

export const setupPWAInstallPrompt = (onInstallable) => {
    window.addEventListener('beforeinstallprompt', (e) => {
        // Prevent the mini-infobar from appearing on mobile
        e.preventDefault();
        // Stash the event so it can be triggered later
        deferredPrompt = e;
        // Notify that app can be installed
        if (onInstallable) {
            onInstallable(true);
        }
    });

    window.addEventListener('appinstalled', () => {
        // Clear the deferredPrompt
        deferredPrompt = null;
        console.log('PWA was installed');
    });
};

export const triggerPWAInstall = async () => {
    if (!deferredPrompt) {
        return { success: false, error: 'Install prompt not available' };
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    // Clear the deferredPrompt
    deferredPrompt = null;

    return { success: outcome === 'accepted', outcome };
};

/**
 * Check if should show install banner
 */
export const shouldShowInstallBanner = () => {
    // Don't show if already installed
    if (isPWA()) return false;

    // Don't show if user dismissed it recently (check localStorage)
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
        const dismissedTime = parseInt(dismissed, 10);
        const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < 7) return false; // Don't show for 7 days
    }

    // Show for iOS Safari or Android with install prompt
    if (isIOSSafari() || deferredPrompt) {
        return true;
    }

    return false;
};

/**
 * Dismiss install banner
 */
export const dismissInstallBanner = () => {
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
};

export default {
    isPWA,
    isIOS,
    isIOSSafari,
    isAndroid,
    canInstallPWA,
    getIOSVersion,
    iosSupportsWebPush,
    notificationsSupported,
    getNotificationPermission,
    showIOSInstallInstructions,
    getDeviceInfo,
    setupPWAInstallPrompt,
    triggerPWAInstall,
    shouldShowInstallBanner,
    dismissInstallBanner
};

